import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { formatDateTime } from '../utils/dateFormat';
import '../styles/AdminNotifications.css';

const AdminNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    type: 'broadcast',
    recipient_id: '',
    title: '',
    content: ''
  });
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    fetchNotifications();
    fetchUsers();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await api.get('/admin/notifications');
      setNotifications(response.data);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || t('errors.loadError'));
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data.filter(u => u.role !== 'admin'));
    } catch (err) {
      // silently fail
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim()) return;

    setSending(true);
    try {
      await api.post('/admin/notifications', {
        title: form.title,
        content: form.content,
        type: form.type,
        recipient_id: form.type === 'individual' ? parseInt(form.recipient_id) : null
      });
      setForm({ type: 'broadcast', recipient_id: '', title: '', content: '' });
      setShowForm(false);
      fetchNotifications();
    } catch (err) {
      alert(err.response?.data?.message || t('admin.notifications.sendError'));
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('confirms.deleteNotification'))) return;
    try {
      await api.delete(`/admin/notifications/${id}`);
      fetchNotifications();
    } catch (err) {
      alert(err.response?.data?.message || t('admin.notifications.deleteError'));
    }
  };

  if (loading) return <div className="loading">{t('common.loading')}</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="admin-notifications">
      <div className="page-header">
        <h1>{t('admin.notifications.title')}</h1>
        <div className="header-actions">
          <button onClick={() => setShowForm(!showForm)} className="new-notif-btn">
            {showForm ? t('common.cancel') : t('admin.notifications.newNotification')}
          </button>
          <button onClick={() => navigate('/admin')} className="back-btn">
            {t('common.backToDashboard')}
          </button>
        </div>
      </div>

      {showForm && (
        <div className="notif-form-card">
          <h2>{t('admin.notifications.sendNotification')}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>{t('admin.notifications.typeLabel')}</label>
              <div className="type-selector">
                <button
                  type="button"
                  className={`type-btn ${form.type === 'broadcast' ? 'active' : ''}`}
                  onClick={() => setForm({ ...form, type: 'broadcast', recipient_id: '' })}
                >
                  {t('admin.notifications.typeGeneral')}
                </button>
                <button
                  type="button"
                  className={`type-btn ${form.type === 'individual' ? 'active' : ''}`}
                  onClick={() => setForm({ ...form, type: 'individual' })}
                >
                  {t('admin.notifications.typeIndividual')}
                </button>
              </div>
            </div>

            {form.type === 'individual' && (
              <div className="form-group">
                <label>{t('admin.notifications.recipientLabel')}</label>
                <select
                  value={form.recipient_id}
                  onChange={(e) => setForm({ ...form, recipient_id: e.target.value })}
                  required
                >
                  <option value="">{t('admin.notifications.recipientPlaceholder')}</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.username} ({u.email})
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-group">
              <label>{t('admin.notifications.titleLabel')}</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder={t('admin.notifications.titlePlaceholder')}
                required
              />
            </div>

            <div className="form-group">
              <label>{t('admin.notifications.contentLabel')}</label>
              <textarea
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                placeholder={t('admin.notifications.contentPlaceholder')}
                rows={5}
                required
              />
            </div>

            <button type="submit" className="send-btn" disabled={sending}>
              {sending ? t('common.sending') : t('common.send')}
            </button>
          </form>
        </div>
      )}

      <div className="notif-history">
        <h2>{t('admin.notifications.history', { count: notifications.length })}</h2>
        {notifications.length === 0 ? (
          <p className="empty-text">{t('admin.notifications.noNotifications')}</p>
        ) : (
          <div className="notif-table">
            <table>
              <thead>
                <tr>
                  <th>{t('common.date')}</th>
                  <th>{t('common.type')}</th>
                  <th>{t('admin.notifications.recipientLabel')}</th>
                  <th>{t('common.title')}</th>
                  <th>{t('common.content')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {notifications.map(notif => (
                  <tr key={notif.id}>
                    <td>{formatDateTime(notif.created_at)}</td>
                    <td>
                      <span className={`type-badge ${notif.type}`}>
                        {notif.type === 'broadcast' ? t('admin.notifications.recipientAll') : t('admin.notifications.recipientIndividual')}
                      </span>
                    </td>
                    <td>{notif.recipient_username || t('admin.notifications.recipientAll')}</td>
                    <td className="notif-title-cell">{notif.title}</td>
                    <td className="notif-content-cell">{notif.content}</td>
                    <td>
                      <button
                        onClick={() => handleDelete(notif.id)}
                        className="delete-btn"
                      >
                        {t('common.delete')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminNotifications;
